import React, { useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, Sparkles, UserPlus } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AccountLookup() {
  const [mode, setMode] = useState("lookup"); // lookup | signup
  const [phone, setPhone] = useState("");
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // signup form state
  const [form, setForm] = useState({ first_name: "", last_name: "", birth_month: 1, phone: "", email: "" });
  const [signupErr, setSignupErr] = useState("");
  const [signupBusy, setSignupBusy] = useState(false);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMember(null);
    if (!phone.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/members/${encodeURIComponent(phone.trim())}/public`);
      setMember(r.data);
    } catch (err) {
      setError(err.response?.status === 404 ? "No account found with that number." : (err.response?.data?.detail || "Lookup failed"));
    } finally {
      setLoading(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setSignupErr(""); setSignupBusy(true);
    try {
      const r = await api.post("/members", { ...form, birth_month: Number(form.birth_month) });
      setMember({ first_name: r.data.first_name, points: r.data.points || 0,
                  trade_credit: r.data.trade_credit || 0, phone: r.data.phone });
      setMode("lookup");
      setPhone(r.data.phone);
    } catch (err) {
      setSignupErr(err.response?.data?.detail || "Failed to create account");
    } finally {
      setSignupBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>AB Member</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">Membership</h1>
        <p className="text-sm max-w-xl mb-8" style={{ color: "var(--ab-text-muted)" }}>
          Look up your µPoints balance using the phone number you signed up with — or create a free account in a minute.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 mb-6" style={{ borderBottom: "1px solid var(--ab-border)" }}>
          <button
            type="button"
            onClick={() => { setMode("lookup"); setError(""); }}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
            style={{
              color: mode === "lookup" ? "var(--ab-primary)" : "var(--ab-text-dim)",
              borderBottom: mode === "lookup" ? "2px solid var(--ab-primary)" : "2px solid transparent",
              marginBottom: "-1px",
            }}
            data-testid="account-tab-lookup"
          >
            Existing Member
          </button>
          <button
            type="button"
            onClick={() => { setMode("signup"); setSignupErr(""); }}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
            style={{
              color: mode === "signup" ? "var(--ab-primary)" : "var(--ab-text-dim)",
              borderBottom: mode === "signup" ? "2px solid var(--ab-primary)" : "2px solid transparent",
              marginBottom: "-1px",
            }}
            data-testid="account-tab-signup"
          >
            Create Account
          </button>
        </div>

        {mode === "lookup" ? (
          <>
            <form onSubmit={submit} className="surface p-2 flex items-stretch mb-6">
              <input className="flex-1 bg-transparent border-0 px-4 py-4 outline-none font-mono text-lg"
                     placeholder="555 123 4567"
                     value={phone}
                     onChange={(e) => setPhone(e.target.value)}
                     autoFocus
                     data-testid="account-lookup-phone" />
              <button type="submit" className="btn-primary px-6" data-testid="account-lookup-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Check"}
              </button>
            </form>
            {error && <div className="surface p-5 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="account-lookup-error">{error}</div>}
          </>
        ) : (
          <form onSubmit={submitSignup} className="surface p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-eyebrow block mb-2">First Name</label>
                <input className="input-terminal" required value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="al-su-first" /></div>
              <div><label className="label-eyebrow block mb-2">Last Name</label>
                <input className="input-terminal" required value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="al-su-last" /></div>
              <div><label className="label-eyebrow block mb-2">Birth Month</label>
                <select className="input-terminal" value={form.birth_month} onChange={(e) => upd("birth_month", e.target.value)} data-testid="al-su-month">
                  {MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
                </select></div>
              <div><label className="label-eyebrow block mb-2">Phone (Account #)</label>
                <input className="input-terminal" required value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="5551234567" data-testid="al-su-phone" /></div>
              <div className="sm:col-span-2"><label className="label-eyebrow block mb-2">Email</label>
                <input type="email" className="input-terminal" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="al-su-email" /></div>
            </div>
            {signupErr && <div className="text-sm" style={{ color: "var(--ab-danger)" }} data-testid="al-su-error">{signupErr}</div>}
            <button type="submit" disabled={signupBusy} className="btn-primary inline-flex items-center gap-2" data-testid="al-su-submit">
              {signupBusy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create Free Account
            </button>
            <p className="text-xs" style={{ color: "var(--ab-text-muted)" }}>
              You&apos;ll get 25 µPoints per $1 spent at AB Microplay, $5 in birthday µPoints every year, exclusive event invites, and early access emails.
            </p>
          </form>
        )}

        {member && (
          <div className="surface p-8 fade-in mt-6" data-testid="account-lookup-result">
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Welcome back</div>
            <div className="font-display text-3xl font-black mb-6">{member.first_name}</div>
            <div className="flex items-end justify-between border-t pt-6 gap-6 flex-wrap" style={{ borderColor: "var(--ab-border)" }}>
              <div>
                <div className="label-eyebrow mb-2">µPoints Balance</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono font-black text-5xl" style={{ color: "var(--ab-primary)" }}>
                    {member.points.toLocaleString()}
                  </span>
                  <span className="text-sm" style={{ color: "var(--ab-text-muted)" }}>µPoints</span>
                </div>
              </div>
              <div data-testid="account-trade-credit">
                <div className="label-eyebrow mb-2">Trade Credit</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono font-black text-5xl" style={{ color: "var(--ab-primary)" }}>
                    ${Number(member.trade_credit || 0).toLocaleString()}
                  </span>
                </div>
              </div>
              <Sparkles size={28} style={{ color: "var(--ab-primary)" }} />
            </div>
            <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--ab-border)" }}>
              <div className="label-eyebrow mb-3">Redemption Tiers</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[{a:2,p:1000},{a:5,p:2500},{a:10,p:5000},{a:20,p:10000}].map((t) => {
                  const can = member.points >= t.p;
                  return (
                    <div key={t.a} className="border p-3" style={{ borderColor: can ? "var(--ab-primary)" : "var(--ab-border)", opacity: can ? 1 : 0.5 }}>
                      <div className="font-mono font-bold text-lg" style={{ color: can ? "var(--ab-primary)" : "var(--ab-text-muted)" }}>${t.a}</div>
                      <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{t.p.toLocaleString()} pts</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
